import { useState, useEffect, useCallback } from 'react';
import { useNavigate, useParams, Link as RouterLink } from 'react-router-dom';
import {
  Box,
  Container,
  Typography,
  Card,
  CardContent,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  IconButton,
  Pagination,
  AppBar,
  Toolbar,
  Link,
  CircularProgress,
  Grid,
  Popover,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import VisibilityIcon from '@mui/icons-material/Visibility';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import axios from 'axios';

function renderPostBody(content, titles, currentId) {
  const text = content || '';
  const nodes = [];
  let rest = text;
  let key = 0;
  const candidates = (titles || [])
    .filter((item) => String(item.id) !== String(currentId) && item.title)
    .sort((a, b) => b.title.length - a.title.length);

  while (rest.length) {
    let best = null;
    for (const item of candidates) {
      const idx = rest.indexOf(item.title);
      if (idx < 0) continue;
      if (!best || idx < best.idx || (idx === best.idx && item.title.length > best.item.title.length)) {
        best = { idx, item };
      }
    }
    if (!best) {
      nodes.push(rest);
      break;
    }
    if (best.idx > 0) nodes.push(rest.slice(0, best.idx));
    nodes.push(
      <Link
        key={`post-link-${key}`}
        component={RouterLink}
        to={`/blog/${best.item.id}`}
        underline="always"
        sx={{ color: '#9ecbff', fontWeight: 600 }}
      >
        {best.item.title}
      </Link>
    );
    key += 1;
    rest = rest.slice(best.idx + best.item.title.length);
  }

  return nodes;
}

export default function BlogPage() {
  const { postId } = useParams();
  const navigate = useNavigate();
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [selectedPost, setSelectedPost] = useState(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editForm, setEditForm] = useState({ title: '', content: '' });
  const [editingId, setEditingId] = useState(null);
  const [isAdmin, setIsAdmin] = useState(false);
  const [sitesAnchorEl, setSitesAnchorEl] = useState(null);
  const [titleIndex, setTitleIndex] = useState([]);

  useEffect(() => {
    const token = localStorage.getItem('token');
    if (token) {
      try {
        const payload = JSON.parse(atob(token.split('.')[1]));
        setIsAdmin(payload.role === 'admin');
      } catch { /* ignore */ }
    }
  }, []);

  const PAGE_SIZE = 9;

  const fetchPosts = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get('/api/community/posts', { params: { exclude_type: 'notice', page, limit: PAGE_SIZE } });
      if (res.data.success) {
        const nextTotal = res.data.data.pagination.totalPages || 1;
        setPosts(res.data.data.posts);
        setTotalPages(nextTotal);
        if (page > nextTotal) setPage(nextTotal);
      }
    } catch (error) {
      console.error('Failed to fetch blog posts:', error);
    } finally {
      setLoading(false);
    }
  }, [page]);

  useEffect(() => { fetchPosts(); }, [fetchPosts]);

  useEffect(() => {
    let cancelled = false;
    axios.get('/api/community/posts', { params: { exclude_type: 'notice', page: 1, limit: 50 } })
      .then((res) => {
        if (cancelled || !res.data.success) return;
        setTitleIndex(res.data.data.posts.map((item) => ({ id: item.id, title: item.title })));
      })
      .catch(() => {});
    return () => { cancelled = true; };
  }, []);

  useEffect(() => {
    if (!postId) {
      setSelectedPost(null);
      return undefined;
    }
    let cancelled = false;
    axios.get(`/api/community/posts/${postId}`)
      .then((res) => {
        if (!cancelled && res.data.success) {
          setSelectedPost(res.data.data);
          window.scrollTo({ top: 0 });
        }
      })
      .catch(() => {
        if (!cancelled) setSelectedPost(null);
      });
    return () => { cancelled = true; };
  }, [postId]);

  const handleCreate = () => {
    setEditingId(null);
    setEditForm({ title: '', content: '' });
    setDialogOpen(true);
  };

  const handleEdit = (post) => {
    setEditingId(post.id);
    setEditForm({ title: post.title, content: post.content });
    setDialogOpen(true);
  };

  const handleDelete = async (id) => {
    if (!window.confirm('정말 삭제하시겠습니까?')) return;
    try {
      const token = localStorage.getItem('token');
      await axios.delete(`/api/community/posts/${id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      fetchPosts();
      setSelectedPost(null);
      if (postId) navigate('/blog');
    } catch {
      alert('삭제에 실패했습니다.');
    }
  };

  const handleSubmit = async () => {
    if (!editForm.title.trim() || !editForm.content.trim()) {
      alert('제목과 내용을 입력해주세요.');
      return;
    }
    try {
      const token = localStorage.getItem('token');
      const headers = { Authorization: `Bearer ${token}` };
      const body = { ...editForm, type: 'free' };
      if (editingId) {
        await axios.put(`/api/community/posts/${editingId}`, body, { headers });
      } else {
        await axios.post('/api/community/posts', body, { headers });
      }
      setDialogOpen(false);
      fetchPosts();
    } catch {
      alert('저장에 실패했습니다.');
    }
  };

  const handlePostClick = (post) => {
    navigate(`/blog/${post.id}`);
  };

  return (
    <Box sx={{ minHeight: '100vh', bgcolor: '#0a0a0a', color: '#fff' }}>
      {/* Header */}
      <AppBar position="sticky" elevation={0} sx={{ backgroundColor: '#0a0a0a', borderBottom: '1px solid #222' }}>
        <Toolbar sx={{ justifyContent: 'space-between' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <Link href="/#/" underline="none" sx={{ display: 'flex', alignItems: 'center' }}>
              <Box component="img" src="/logo.png" alt="KOAI" sx={{ height: 36 }} />
            </Link>
            <Typography variant="h6" sx={{ color: 'white', fontWeight: 600 }}>
              블로그
            </Typography>
          </Box>
          <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
            <Link href="/#/community" underline="none" sx={{ color: '#aaa', fontSize: '0.9rem', '&:hover': { color: '#fff' } }}>커뮤니티</Link>
            <Link href="/#/" underline="none" sx={{ color: '#aaa', fontSize: '0.9rem', '&:hover': { color: '#fff' } }}>홈</Link>
            <Typography
              onClick={(e) => setSitesAnchorEl(e.currentTarget)}
              sx={{ color: '#aaa', fontSize: '0.9rem', cursor: 'pointer', '&:hover': { color: '#fff' } }}
            >
              Our Sites
            </Typography>
            <Popover
              open={Boolean(sitesAnchorEl)}
              anchorEl={sitesAnchorEl}
              onClose={() => setSitesAnchorEl(null)}
              anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
              transformOrigin={{ vertical: 'top', horizontal: 'center' }}
              PaperProps={{ sx: { bgcolor: '#1a1a1a', border: '1px solid #333', mt: 1, p: 1.5 } }}
            >
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, minWidth: 180 }}>
                <Link href="http://kznetwork.co.kr/" target="_blank" rel="noopener noreferrer" underline="hover" sx={{ color: '#ccc', fontSize: '0.85rem', px: 1, py: 0.5, borderRadius: 1, '&:hover': { color: '#fff', bgcolor: '#333' } }}>
                  kznetwork.co.kr
                </Link>
                <Link href="https://brandgrowth.store/" target="_blank" rel="noopener noreferrer" underline="hover" sx={{ color: '#ccc', fontSize: '0.85rem', px: 1, py: 0.5, borderRadius: 1, '&:hover': { color: '#fff', bgcolor: '#333' } }}>
                  brandgrowth.store
                </Link>
                <Link href="https://aiquote-generator.pages.dev/" target="_blank" rel="noopener noreferrer" underline="hover" sx={{ color: '#ccc', fontSize: '0.85rem', px: 1, py: 0.5, borderRadius: 1, '&:hover': { color: '#fff', bgcolor: '#333' } }}>
                  AI 견적 생성기
                </Link>
              </Box>
            </Popover>
            {isAdmin ? (
              <Button size="small" variant="outlined" sx={{ color: '#aaa', borderColor: '#555', fontSize: '0.75rem' }} onClick={() => { localStorage.removeItem('token'); setIsAdmin(false); }}>로그아웃</Button>
            ) : (
              <Button size="small" variant="outlined" sx={{ color: '#aaa', borderColor: '#555', fontSize: '0.75rem' }} onClick={() => window.location.href = '/#/'}>관리자 로그인</Button>
            )}
          </Box>
        </Toolbar>
      </AppBar>

      <Container maxWidth="lg" sx={{ py: 6 }}>
        {postId ? (
          /* Post Detail */
          <Box>
            <Button startIcon={<ArrowBackIcon />} onClick={() => navigate('/blog')} sx={{ mb: 3, color: '#aaa' }}>
              목록으로
            </Button>
            <Box sx={{ maxWidth: 800, mx: 'auto' }}>
              {!selectedPost ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', py: 10 }}>
                  <CircularProgress sx={{ color: '#666' }} />
                </Box>
              ) : (
                <>
                  <Typography variant="h4" fontWeight={700} sx={{ mb: 2 }}>
                    {selectedPost.title}
                  </Typography>
                  <Box sx={{ display: 'flex', gap: 3, mb: 4, color: '#888' }}>
                    <Typography variant="body2">{selectedPost.authorName}</Typography>
                    <Typography variant="body2">{new Date(selectedPost.createdAt).toLocaleDateString('ko-KR')}</Typography>
                    <Typography variant="body2">조회 {selectedPost.viewCount}</Typography>
                  </Box>
                  <Typography component="div" variant="body1" sx={{ whiteSpace: 'pre-wrap', lineHeight: 2, color: '#ddd' }}>
                    {renderPostBody(selectedPost.content, titleIndex, selectedPost.id)}
                  </Typography>
                  {isAdmin && (
                    <Box sx={{ mt: 4, display: 'flex', gap: 1 }}>
                      <Button size="small" variant="outlined" startIcon={<EditIcon />} onClick={() => handleEdit(selectedPost)} sx={{ color: '#aaa', borderColor: '#444' }}>수정</Button>
                      <Button size="small" variant="outlined" color="error" startIcon={<DeleteIcon />} onClick={() => handleDelete(selectedPost.id)}>삭제</Button>
                    </Box>
                  )}
                </>
              )}
            </Box>
          </Box>
        ) : (
          /* Blog List */
          <Box>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 4 }}>
              <Typography variant="h4" fontWeight={700}>최신 글</Typography>
              {isAdmin && (
                <Button variant="contained" startIcon={<AddIcon />} onClick={handleCreate} sx={{ bgcolor: '#333', '&:hover': { bgcolor: '#444' } }}>
                  새 글 작성
                </Button>
              )}
            </Box>

            {loading ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', py: 10 }}>
                <CircularProgress sx={{ color: '#666' }} />
              </Box>
            ) : posts.length === 0 ? (
              <Box sx={{ textAlign: 'center', py: 10 }}>
                <Typography color="#666">아직 작성된 글이 없습니다.</Typography>
              </Box>
            ) : (
              <Grid container spacing={3}>
                  {posts.map(post => (
                    <Grid item xs={12} sm={6} md={4} key={post.id}>
                      <Card
                        sx={{
                          bgcolor: '#141414',
                          border: '1px solid #222',
                          height: '100%',
                          cursor: 'pointer',
                          transition: 'all 0.2s',
                          '&:hover': { borderColor: '#444', transform: 'translateY(-2px)' }
                        }}
                        onClick={() => handlePostClick(post)}
                      >
                        <CardContent sx={{ p: 3 }}>
                          <Typography variant="h6" fontWeight={600} sx={{ color: '#fff', mb: 1.5, display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden' }}>
                            {post.title}
                          </Typography>
                          <Typography variant="body2" sx={{ color: '#888', mb: 2, display: '-webkit-box', WebkitLineClamp: 3, WebkitBoxOrient: 'vertical', overflow: 'hidden', lineHeight: 1.6 }}>
                            {post.content}
                          </Typography>
                          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', color: '#666' }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                              <CalendarTodayIcon sx={{ fontSize: 14 }} />
                              <Typography variant="caption">{new Date(post.createdAt).toLocaleDateString('ko-KR')}</Typography>
                            </Box>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                              <VisibilityIcon sx={{ fontSize: 14 }} />
                              <Typography variant="caption">{post.viewCount}</Typography>
                            </Box>
                          </Box>
                          {isAdmin && (
                            <Box sx={{ mt: 2, display: 'flex', gap: 0.5 }}>
                              <IconButton size="small" onClick={(e) => { e.stopPropagation(); handleEdit(post); }} sx={{ color: '#666' }}><EditIcon fontSize="small" /></IconButton>
                              <IconButton size="small" onClick={(e) => { e.stopPropagation(); handleDelete(post.id); }} sx={{ color: '#666' }}><DeleteIcon fontSize="small" /></IconButton>
                            </Box>
                          )}
                        </CardContent>
                      </Card>
                    </Grid>
                  ))}
                </Grid>
            )}

            {!loading && totalPages > 1 && (
              <Box sx={{ display: 'flex', justifyContent: 'center', mt: 5 }}>
                <Pagination
                  count={totalPages}
                  page={page}
                  onChange={(_, v) => {
                    setPage(v);
                    window.scrollTo({ top: 0, behavior: 'smooth' });
                  }}
                  sx={{
                    '& .MuiPaginationItem-root': { color: '#ccc' },
                    '& .Mui-selected': { bgcolor: '#333 !important', color: '#fff' },
                    '& .Mui-disabled': { color: '#444' }
                  }}
                />
              </Box>
            )}
          </Box>
        )}
      </Container>

      {/* Create/Edit Dialog */}
      <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} maxWidth="md" fullWidth PaperProps={{ sx: { bgcolor: '#1a1a1a', color: '#fff' } }}>
        <DialogTitle>{editingId ? '글 수정' : '새 글 작성'}</DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
            <TextField
              label="제목"
              fullWidth
              value={editForm.title}
              onChange={(e) => setEditForm({ ...editForm, title: e.target.value })}
              InputProps={{ sx: { color: '#fff' } }}
              InputLabelProps={{ sx: { color: '#888' } }}
              sx={{ '& .MuiOutlinedInput-root': { '& fieldset': { borderColor: '#333' } } }}
            />
            <TextField
              label="내용"
              fullWidth
              multiline
              rows={12}
              value={editForm.content}
              onChange={(e) => setEditForm({ ...editForm, content: e.target.value })}
              InputProps={{ sx: { color: '#fff' } }}
              InputLabelProps={{ sx: { color: '#888' } }}
              sx={{ '& .MuiOutlinedInput-root': { '& fieldset': { borderColor: '#333' } } }}
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDialogOpen(false)} sx={{ color: '#888' }}>취소</Button>
          <Button variant="contained" onClick={handleSubmit} sx={{ bgcolor: '#333' }}>저장</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
